import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import SectionWrapper from '../shared/SectionWrapper';
import { FloatingOrbs, FloatingParticles } from '../shared/FloatingElements';

/* ------------------------------------------------------------------ */
/*  Animated flowing connector between steps                           */
/* ------------------------------------------------------------------ */
const FlowingConnector = ({ delay = 0 }) => (
  <div
    className="flex-1 min-w-[16px] max-w-[70px] md:max-w-[100px] mx-1 md:mx-1.5 flex items-center relative"
    style={{ marginTop: -14, height: 20 }}
  >
    <svg width="100%" height="20" className="overflow-visible">
      {/* Static dashed track */}
      <line
        x1="0" y1="10" x2="100%" y2="10"
        stroke="black"
        strokeWidth="2"
        strokeDasharray="4 4"
        className="opacity-20 dark:opacity-10 dark:[stroke:white]"
      />
      {/* Animated flowing line */}
      <line
        x1="0" y1="10" x2="100%" y2="10"
        stroke="url(#flowGradient)"
        strokeWidth="2"
        strokeDasharray="8 16"
        strokeLinecap="round"
        style={{
          animation: `flowDash 1.5s linear infinite`,
          animationDelay: `${delay}s`,
        }}
      />
      {/* Gradient def */}
      <defs>
        <linearGradient id="flowGradient" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#a78bfa" />
        </linearGradient>
      </defs>
    </svg>
    {/* Arrow tip */}
    <svg
      width="7"
      height="7"
      viewBox="0 0 8 8"
      fill="none"
      className="absolute -right-0.5 top-1/2 -translate-y-1/2"
    >
      <path d="M1 1l5 3-5 3" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Workflow Steps — Real ESGCaffe Platform Flow                       */
/*  Colors follow tenant-admin icon-container palette                  */
/* ------------------------------------------------------------------ */
const steps = [
  {
    id: 1,
    label: 'Onboard',
    title: 'Company Setup & Onboarding',
    description:
      'Register your company, set up organizational structure, add locations and facilities, invite team members with role-based permissions.',
    color: '#3b82f6',
    iconBg: 'bg-blue-50 dark:bg-blue-950/50',
    iconBorder: 'border-blue-200 dark:border-blue-800',
    activeBg: 'bg-blue-50 dark:bg-blue-900/30',
    activeText: 'text-blue-600 dark:text-blue-400',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    details: [
      'Company profile & industry',
      'Org chart & departments',
      'Multi-location tracking',
      'Team roles & permissions',
    ],
  },
  {
    id: 2,
    label: 'Collect',
    title: 'ESG Data Collection',
    description:
      'Answer framework-specific questions across Environmental (carbon, energy, water, waste), Social (workforce, DEI, health & safety), and Governance (board, ethics, risk) categories.',
    color: '#8b5cf6',
    iconBg: 'bg-purple-50 dark:bg-purple-950/50',
    iconBorder: 'border-purple-200 dark:border-purple-800',
    activeBg: 'bg-purple-50 dark:bg-purple-900/30',
    activeText: 'text-purple-600 dark:text-purple-400',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
    details: [
      'Environmental: Carbon, Energy, Water, Waste',
      'Social: Workforce, DEI, Health & Safety',
      'Governance: Board, Ethics, Risk',
      'Supply Chain: Suppliers, Scope 3',
    ],
  },
  {
    id: 3,
    label: 'Analyze',
    title: 'AI-Powered Analysis',
    description:
      'ESGCaffe AI engine processes your data — performs double materiality assessment, identifies gaps, benchmarks against industry peers, and maps data to 35+ frameworks automatically.',
    color: '#06b6d4',
    iconBg: 'bg-cyan-50 dark:bg-cyan-950/50',
    iconBorder: 'border-cyan-200 dark:border-cyan-800',
    activeBg: 'bg-cyan-50 dark:bg-cyan-900/30',
    activeText: 'text-cyan-600 dark:text-cyan-400',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#06b6d4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a4 4 0 0 1 4 4c0 1.5-.8 2.8-2 3.5v1.5h-4V9.5C8.8 8.8 8 7.5 8 6a4 4 0 0 1 4-4z" />
        <path d="M10 11v2a2 2 0 0 0 4 0v-2" />
        <circle cx="12" cy="18" r="3" />
        <path d="M12 15v-2" />
        <path d="M9 18H5" />
        <path d="M19 18h-4" />
      </svg>
    ),
    details: [
      'Double materiality assessment',
      'AI gap detection & recommendations',
      'Competitive benchmarking',
      'Auto framework mapping (35+)',
    ],
  },
  {
    id: 4,
    label: 'Visualize',
    title: 'Dashboards & Insights',
    description:
      'Interactive dashboards with real-time ESG KPIs, filterable by framework, location, and category. Track targets, view trends, and monitor progress across all ESG pillars.',
    color: '#34d399',
    iconBg: 'bg-green-50 dark:bg-green-950/50',
    iconBorder: 'border-green-200 dark:border-green-800',
    activeBg: 'bg-green-50 dark:bg-green-900/30',
    activeText: 'text-green-600 dark:text-green-400',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18" />
        <path d="M18 17V9" />
        <path d="M13 17V5" />
        <path d="M8 17v-3" />
      </svg>
    ),
    details: [
      'Real-time ESG KPI dashboards',
      'Framework & location filters',
      'Target tracking & progress',
      'Competitive analytics',
    ],
  },
  {
    id: 5,
    label: 'Report',
    title: 'Report Generation',
    description:
      'Generate compliance-ready reports in multiple formats — branded PDF, Word (Docx), XBRL for regulatory filing, Excel, PowerPoint. Choose themes and export instantly.',
    color: '#f59e0b',
    iconBg: 'bg-orange-50 dark:bg-orange-950/50',
    iconBorder: 'border-orange-200 dark:border-orange-800',
    activeBg: 'bg-orange-50 dark:bg-orange-900/30',
    activeText: 'text-orange-600 dark:text-orange-400',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <path d="M12 18v-6" />
        <path d="M8 15l4 3 4-3" />
      </svg>
    ),
    details: [
      'PDF, Word, Excel, PowerPoint',
      'XBRL / iXBRL for SEC filings',
      'Custom branded themes',
      'Multi-framework reports',
    ],
  },
  {
    id: 6,
    label: 'Comply',
    title: 'Compliance & Filing',
    description:
      'Submit reports to regulators — SEC (XBRL), India NSE (BRSR), EU (CSRD/ESRS). Track filing status, manage deadlines, publish to stakeholders and investors.',
    color: '#ef4444',
    iconBg: 'bg-red-50 dark:bg-red-950/50',
    iconBorder: 'border-red-200 dark:border-red-800',
    activeBg: 'bg-red-50 dark:bg-red-900/30',
    activeText: 'text-red-600 dark:text-red-400',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
    details: [
      'SEC, NSE, EU CSRD filings',
      'Deadline management',
      'Status tracking & audit trail',
      'Stakeholder publishing',
    ],
  },
];

const total = steps.length;

/* ------------------------------------------------------------------ */
/*  Step Card — stat-card style from tenant-admin                      */
/* ------------------------------------------------------------------ */
const StepCard = ({ step, isActive }) => (
  <div
    className={`relative rounded-2xl transition-all duration-500 shrink-0 border ${
      isActive
        ? 'bg-white dark:bg-[hsl(220,39%,11%)] shadow-md border-[hsl(214,32%,91%)] dark:border-[hsl(218,20%,25%)]'
        : 'bg-white/60 dark:bg-[hsl(220,39%,11%,0.5)] shadow-sm scale-[0.96] opacity-50 border-[hsl(214,32%,91%)] dark:border-[hsl(218,20%,25%)]'
    }`}
    style={{ width: '100%' }}
  >
    <div className="p-6 md:p-8 lg:p-10">
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-10">
        {/* Left: Icon + Title + Description */}
        <div className="flex-1">
          <div className="flex items-center gap-4 mb-4">
            {/* Icon container — matches tenant-admin .icon-container pattern */}
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center border-2 ${step.iconBg} ${step.iconBorder}`}
            >
              {step.icon}
            </div>
            <h3
              className="text-xl md:text-2xl font-semibold tracking-tight leading-none"
              style={{ color: 'hsl(222, 47%, 11%)' }}
            >
              <span className="dark:text-[hsl(210,40%,98%)]">{step.title}</span>
            </h3>
          </div>

          <p
            className="text-sm leading-relaxed"
            style={{ color: 'hsl(215, 16%, 47%)' }}
          >
            <span className="dark:text-[hsl(218,11%,65%)]">{step.description}</span>
          </p>
        </div>

        {/* Right: Feature checklist */}
        <div className="lg:w-[280px] flex-shrink-0">
          <div
            className="text-[11px] font-semibold uppercase tracking-wider mb-3"
            style={{ color: 'hsl(215, 16%, 57%)' }}
          >
            <span className="dark:text-[hsl(218,14%,50%)]">Key Features</span>
          </div>
          <ul className="space-y-2.5">
            {step.details.map((detail, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="mt-0.5 flex-shrink-0"
                  stroke={step.color}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                <span className="text-sm text-[hsl(222,47%,11%)] dark:text-[hsl(210,40%,98%)]">
                  {detail}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */
const HowItWorksSection = () => {
  const [active, setActive] = useState(0);

  const goNext = useCallback(() => setActive((p) => (p + 1) % total), []);
  const goPrev = useCallback(() => setActive((p) => (p - 1 + total) % total), []);

  // Auto-play every 5 seconds
  useEffect(() => {
    const timer = setInterval(goNext, 5000);
    return () => clearInterval(timer);
  }, [goNext]);

  return (
    <SectionWrapper
      id="how-it-works"
      bgClass="bg-[hsl(210,40%,98%)] dark:bg-[hsl(222,84%,5%)]"
      padding="py-10 md:py-12"
      className="relative"
    >
      {/* Floating background — platform orb gradients */}
      <FloatingOrbs
        orbs={[
          { size: 280, color: 'hsla(217,91%,60%,0.08)', top: '8%', left: '82%', drift: 22, duration: 10, delay: 0 },
          { size: 240, color: 'hsla(262,83%,58%,0.06)', top: '55%', left: '-4%', drift: 18, duration: 12, delay: 2 },
          { size: 200, color: 'hsla(158,64%,52%,0.05)', top: '30%', left: '45%', drift: 14, duration: 11, delay: 3 },
        ]}
      />
      <FloatingParticles count={14} color="#3b82f6" theme="light" />

      {/* Flowing dash animation */}
      <style>{`
        @keyframes flowDash {
          0% { stroke-dashoffset: 24; }
          100% { stroke-dashoffset: 0; }
        }
      `}</style>

      {/* Header */}
      <motion.div
        className="text-center mb-14 md:mb-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Badge — platform primary gradient */}
        <span
          className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide uppercase text-white"
          style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #a78bfa 100%)' }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse" />
          How It Works
        </span>

        <h2
          className="mt-6 font-extrabold tracking-tight text-gray-900 dark:text-white leading-tight"
          style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)' }}
        >
          From Signup to Compliance{' '}
          <span
            className="bg-clip-text text-transparent"
            style={{ backgroundImage: 'linear-gradient(135deg, #3b82f6 0%, #a78bfa 100%)' }}
          >
            in 6 Steps
          </span>
        </h2>

        <p className="mt-4 max-w-xl mx-auto text-sm text-[hsl(215,16%,47%)] dark:text-[hsl(218,11%,65%)] leading-relaxed">
          The complete ESG journey — onboard your company, collect data, let AI analyze it, and file compliance reports.
        </p>
      </motion.div>

      {/* ============================================================ */}
      {/*  Steps row + Carousel                                         */}
      {/* ============================================================ */}
      <motion.div
        className="relative max-w-4xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* ── Step circles connected by black dotted lines ── */}
        <div className="flex items-center justify-center gap-0 mb-10 px-2 overflow-x-auto">
          {steps.map((step, i) => (
            <React.Fragment key={step.id}>
              {/* Step node */}
              <button
                onClick={() => setActive(i)}
                className="relative flex flex-col items-center flex-shrink-0 group cursor-pointer focus:outline-none"
              >
                {/* Icon container — matches tenant-admin icon-container-* */}
                <div
                  className={`relative w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center border-2 transition-all duration-300 ${
                    i === active
                      ? `${step.iconBg} ${step.iconBorder} shadow-md scale-110`
                      : 'bg-white dark:bg-[hsl(220,39%,11%)] border-[hsl(214,32%,91%)] dark:border-[hsl(218,20%,25%)] group-hover:shadow-sm group-hover:scale-105'
                  }`}
                >
                  {step.icon}
                </div>

                {/* Label */}
                <span
                  className={`mt-2 text-[10px] md:text-[11px] font-semibold uppercase tracking-wider transition-colors duration-300 ${
                    i === active
                      ? step.activeText
                      : 'text-[hsl(215,16%,57%)] dark:text-[hsl(218,14%,50%)]'
                  }`}
                >
                  {step.label}
                </span>
              </button>

              {/* Animated flowing connector */}
              {i < steps.length - 1 && (
                <FlowingConnector delay={i * 0.25} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* ── Carousel ── */}
        <div className="relative">
          {/* Prev / Next — platform style buttons */}
          <button
            onClick={goPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-14 z-10 w-9 h-9 rounded-xl bg-white dark:bg-[hsl(220,39%,11%)] border border-[hsl(214,32%,91%)] dark:border-[hsl(218,20%,25%)] flex items-center justify-center text-[hsl(215,16%,57%)] hover:border-blue-300 hover:text-blue-600 dark:hover:border-blue-600 dark:hover:text-blue-400 transition-colors shadow-sm"
            aria-label="Previous step"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            onClick={goNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-14 z-10 w-9 h-9 rounded-xl bg-white dark:bg-[hsl(220,39%,11%)] border border-[hsl(214,32%,91%)] dark:border-[hsl(218,20%,25%)] flex items-center justify-center text-[hsl(215,16%,57%)] hover:border-blue-300 hover:text-blue-600 dark:hover:border-blue-600 dark:hover:text-blue-400 transition-colors shadow-sm"
            aria-label="Next step"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>

          {/* Sliding cards */}
          <div className="overflow-hidden rounded-2xl">
            <div
              className="flex transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
              style={{ transform: `translateX(-${active * 100}%)` }}
            >
              {steps.map((step, idx) => (
                <div key={step.id} className="w-full shrink-0 px-1">
                  <StepCard step={step} isActive={idx === active} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Dot indicators — primary blue active ── */}
        <div className="flex justify-center gap-2 mt-8">
          {steps.map((step, idx) => (
            <button
              key={step.id}
              onClick={() => setActive(idx)}
              className={`h-2 rounded-full transition-all duration-300 ${
                idx === active
                  ? 'w-7'
                  : 'w-2 bg-[hsl(214,32%,91%)] dark:bg-[hsl(218,20%,25%)] hover:bg-[hsl(215,16%,57%)]'
              }`}
              style={idx === active ? { background: 'linear-gradient(90deg, #3b82f6, #a78bfa)' } : undefined}
              aria-label={`Go to step ${step.id}: ${step.label}`}
            />
          ))}
        </div>

        {/* Counter */}
        <div className="text-center mt-2.5 text-xs text-[hsl(215,16%,57%)] dark:text-[hsl(218,14%,50%)] font-medium">
          {active + 1} / {total}
        </div>
      </motion.div>
    </SectionWrapper>
  );
};

export default HowItWorksSection;
